import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  InputAdornment,
  IconButton,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import './MenuSearchBar.css';

const SearchIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>;
const FilterIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>;
const ClearIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>;
const HistoryIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>;
const TrendingIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>;
const RECENT_SEARCHES_KEY = 'menu_recent_searches';
const MAX_RECENT = 5;

const MenuSearchBar = ({
  value,
  onChange,
  onClear,
  suggestions = [],
  popularItems = [],
  placeholder,
  isSearching,
  onFilterClick,
  inputRef,
  isRTL,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const open = Boolean(anchorEl) && (value?.length > 0 || (value?.length === 0 && (recentSearches.length > 0 || popularItems.length > 0)));
  const showSuggestions = value?.length > 0 && suggestions.length > 0;
  const showRecentOrPopular = value?.length === 0 && (recentSearches.length > 0 || popularItems.length > 0);

  const handleFocus = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSuggestion = (text) => {
    onChange(text);
    handleClose();
    if (text.trim()) {
      const next = [text.trim(), ...recentSearches.filter((s) => s !== text.trim())].slice(0, MAX_RECENT);
      setRecentSearches(next);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
      } catch {}
    }
  };

  const handleSelectPopular = (name) => {
    onChange(name);
    handleClose();
  };

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(handleClose, 15000);
    return () => clearTimeout(id);
  }, [open]);

  return (
    <Box className="menu-search-bar" sx={{ width: '100%' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        placeholder={placeholder || t('menu.search.placeholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={() => setTimeout(handleClose, 180)}
        inputProps={{
          // role=combobox is required for aria-expanded / aria-autocomplete /
          // aria-controls to be valid on a plain text input. Without it axe
          // flags aria-allowed-attr (critical).
          role: 'combobox',
          'aria-label': placeholder || t('menu.search.placeholder'),
          'aria-autocomplete': 'list',
          'aria-expanded': open,
          'aria-controls': open ? 'menu-search-popover' : undefined,
          // Mobile keyboards: show the search-flavored layout and a search
          // affordance on Enter instead of a generic Return key.
          inputMode: 'search',
          enterKeyHint: 'search',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box component="span" sx={{ color: 'text.secondary', display: 'flex' }}><SearchIcon /></Box>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isSearching && (
                <Box component="span" sx={{ mr: 0.5 }} aria-hidden>
                  …
                </Box>
              )}
              {onFilterClick && (
                <IconButton
                  size="small"
                  onClick={onFilterClick}
                  aria-label={t('common.filter')}
                  sx={{ mr: 0.5 }}
                >
                  <FilterIcon />
                </IconButton>
              )}
              {value ? (
                <IconButton
                  size="small"
                  onClick={() => { onChange(''); onClear?.(); }}
                  aria-label={t('menu.search.clear')}
                >
                  <ClearIcon />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            backgroundColor: 'action.hover',
            transition: 'background-color 0.2s, box-shadow 0.2s',
            '&:hover': { backgroundColor: 'action.selected' },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
              boxShadow: 2,
            },
          },
        }}
      />

      <Popper
        id="menu-search-popover"
        className="menu-search-bar-popper"
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ width: anchorEl?.offsetWidth }}
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1300 }}
      >
        <Paper elevation={4} sx={{ maxHeight: 320, overflow: 'auto', borderRadius: 2 }}>
          {showSuggestions && (
            <List dense>
              {suggestions.slice(0, 8).map((item, i) => (
                <ListItemButton
                  key={`${item.id}-${i}`}
                  onClick={() => handleSelectSuggestion(item.name)}
                  aria-label={item.name}
                >
                  <ListItemText primary={item.name} secondary={item.section} />
                </ListItemButton>
              ))}
            </List>
          )}
          {showRecentOrPopular && !showSuggestions && (
            <>
              {recentSearches.length > 0 && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HistoryIcon sx={{ fontSize: 16 }} />
                    {t('menu.search.recentSearches', 'Recent searches')}
                  </Typography>
                  <List dense disablePadding>
                    {recentSearches.slice(0, 5).map((s, i) => (
                      <ListItemButton key={i} onClick={() => handleSelectSuggestion(s)}>
                        <ListItemText primary={s} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
              {popularItems.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingIcon />
                    {t('menu.search.popularItems', 'Popular items')}
                  </Typography>
                  <List dense disablePadding>
                    {popularItems.slice(0, 5).map((item) => (
                      <ListItemButton key={item.id} onClick={() => handleSelectPopular(item.name)}>
                        <ListItemText primary={item.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Popper>
    </Box>
  );
};

export default MenuSearchBar;
